import React, { useEffect } from 'react';
import { ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LoadingComponent from './loading';
import useJobPolling from './hooks/useJobPolling';

export default function HeavyAiJobProgress() {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId } = useParams();
  const {
    title = 'Processing AI job',
    successPath = '/dashboard',
    successState = {},
    backPath = '/dashboard',
  } = location.state || {};

  const { error, job, progress, stage, status, isComplete, isFailed } = useJobPolling(jobId, {
    enabled: Boolean(jobId),
  });

  const recentLogs = Array.isArray(job?.logs) ? job.logs.slice(-6) : [];
  const isWaitingForWorker = Boolean(job?.debug?.waitingForWorker);

  useEffect(() => {
    if (!isComplete) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      navigate(successPath, {
        replace: true,
        state: {
          ...successState,
          heavyAiJobId: jobId,
          heavyAiJobResult: job?.result || null,
          heavyAiRefreshKey: Date.now(),
        },
      });
    }, 1200);

    return () => window.clearTimeout(timeoutId);
  }, [isComplete, job, jobId, navigate, successPath, successState]);

  if (isFailed) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-6">
        <div className="max-w-xl w-full rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 shadow-2xl">
          <div className="flex items-center gap-4 mb-6">
            <div className="rounded-2xl bg-red-500/20 p-3">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-300" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">Job failed</h1>
              <p className="text-sm text-white/60">The worker reported an error while processing this request.</p>
            </div>
          </div>

          <div className="rounded-2xl bg-black/30 border border-white/10 p-4 text-sm text-white/80 whitespace-pre-wrap">
            {error || job?.failedReason || 'An unknown error occurred.'}
          </div>

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={() => navigate(backPath, { replace: true })}
              className="inline-flex items-center gap-2 rounded-xl bg-white text-slate-950 px-4 py-2 font-medium hover:bg-slate-200 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <LoadingComponent
      label={isComplete ? 'Completed. Redirecting...' : title}
      progress={isComplete ? 100 : progress}
      stage={isComplete ? 'Completed' : (isWaitingForWorker ? 'Queued - waiting for worker to start this job' : stage || status)}
      detail={jobId ? `Job ${jobId}${job?.processedOn ? ' • Worker active' : ''}` : 'Preparing job'}
      logs={recentLogs}
      showProcessDocAnimation
    />
  );
}
